import Alpine from 'alpinejs'
import { thumb, large } from './thumb.js'
import { formatDuration } from './duration.js'

const API = '/.netlify/functions'
const CONCURRENCY = 3
const THUMB_CONCURRENCY = 2
// 15 tiles + the "+N more" counter = an even 16-tile (4×4) grid
const PREVIEW_LIMIT = 15

// Browsers often report an empty file.type for formats like HEIC —
// fall back to the extension so iPhone photos aren't silently dropped
const EXT_TYPES = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', gif: 'image/gif',
  webp: 'image/webp', heic: 'image/heic', heif: 'image/heif', avif: 'image/avif',
  mp4: 'video/mp4', m4v: 'video/x-m4v', mov: 'video/quicktime', webm: 'video/webm',
  avi: 'video/x-msvideo', mkv: 'video/x-matroska', '3gp': 'video/3gpp'
}

const fileType = (file) =>
  file.type || EXT_TYPES[file.name.split('.').pop().toLowerCase()] || ''

// File objects live outside Alpine state — deep-proxying hundreds of
// Files (and re-rendering as they mutate) is what made big batches slow
const rawFiles = new Map()

const PREVIEW_SIZE = 320

// Content hashes make uploads idempotent (the server derives the object
// key from the hash, so the same file never lands twice). Whole-file
// hashing would need the entire file in memory, which crashes mobile
// Safari on multi-GB videos — large files hash size + head + tail instead.
const HASH_FULL_LIMIT = 32 * 1024 * 1024
const HASH_SAMPLE = 2 * 1024 * 1024

async function hashFile(file) {
  try {
    let buffer
    if (file.size <= HASH_FULL_LIMIT) {
      buffer = await file.arrayBuffer()
    } else {
      const head = new Uint8Array(await file.slice(0, HASH_SAMPLE).arrayBuffer())
      const tail = new Uint8Array(await file.slice(file.size - HASH_SAMPLE).arrayBuffer())
      const size = new TextEncoder().encode(String(file.size))
      const combined = new Uint8Array(head.length + tail.length + size.length)
      combined.set(head, 0)
      combined.set(tail, head.length)
      combined.set(size, head.length + tail.length)
      buffer = combined
    }
    const digest = await crypto.subtle.digest('SHA-256', buffer)
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  } catch {
    return null // server falls back to a random key
  }
}

// Gallery order is a shuffle seeded per page load: each visit sees a
// different order, but the 60s refetch keeps the grid stable mid-visit
// and new uploads slot in without reshuffling everything.
const SHUFFLE_SEED = Math.floor(Math.random() * 0xffffffff)

function shuffleRank(id) {
  let h = SHUFFLE_SEED
  for (let i = 0; i < id.length; i++) {
    h ^= id.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return h >>> 0
}

// Grid videos only fetch (metadata + first frame) once they scroll near
// the viewport. Before this, every video tile started downloading on
// render and competed with the thumbnails for bandwidth on first load.
const videoObserver = new IntersectionObserver((entries) => {
  entries.forEach((e) => {
    const el = e.target
    if (!el.isConnected) return videoObserver.unobserve(el)
    if (!e.isIntersecting) return
    el.preload = 'metadata'
    el.src = el.dataset.src
    videoObserver.unobserve(el)
  })
}, { rootMargin: '600px' })

// Downscaled preview blob. Rendering the raw file as a tile forces the
// browser to decode the full-resolution photo (~100MB+ each), which is
// what crashed mobile Safari on large selections.
async function makeThumb(file) {
  try {
    const bitmap = await createImageBitmap(file)
    const scale = Math.min(1, PREVIEW_SIZE / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.7))
    return blob && URL.createObjectURL(blob)
  } catch {
    // Fall back to an <img> decode — Safari can decode HEIC this way
    // even when createImageBitmap refuses it
    const url = URL.createObjectURL(file)
    try {
      const img = new Image()
      img.src = url
      await img.decode()
      const scale = Math.min(1, PREVIEW_SIZE / Math.max(img.naturalWidth, img.naturalHeight))
      const canvas = document.createElement('canvas')
      canvas.width = Math.max(1, Math.round(img.naturalWidth * scale))
      canvas.height = Math.max(1, Math.round(img.naturalHeight * scale))
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.7))
      return blob && URL.createObjectURL(blob)
    } catch {
      return null // undecodable format — the tile shows a placeholder
    } finally {
      URL.revokeObjectURL(url)
    }
  }
}

Alpine.data('share', () => ({
  partyId: new URLSearchParams(window.location.search).get('p') || '',
  items: [],
  error: '',
  notice: '',
  active: 0,
  queue: [],
  thumbActive: 0,
  thumbQueue: [],
  galleryFiles: [],
  heroTiles: [],
  galleryLoaded: false,
  // Server timestamp of the listing on screen. The listing is cached at
  // the edge, so a refresh can hand back something older than what we
  // already rendered (or older than an upload/hide we just made).
  galleryGeneratedAt: 0,
  galleryLimit: 36,
  mediaFilter: 'all',
  galleryInView: false,
  lightboxIndex: null,
  removing: false,
  // Video durations keyed by file id, captured from each tile's
  // loadedmetadata event (preload="metadata" fetches them anyway)
  durations: {},

  init() {
    this.refreshGallery()
    setInterval(() => {
      if (!this.uploading) this.refreshGallery()
    }, 60000)

    // Render more gallery tiles as the bottom of the grid approaches
    this.$nextTick(() => {
      const sentinel = this.$refs.moreSentinel
      if (!sentinel) return
      new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) this.galleryLimit += 36
      }, { rootMargin: '1200px' }).observe(sentinel)

      // The floating filter bar only shows while the wall is on screen.
      // The hero above is exactly one viewport tall, so the gallery's top
      // edge touches the fold before any scrolling — the negative margin
      // keeps the bar hidden until the wall fills the lower third.
      const gallery = this.$refs.gallerySection
      if (!gallery) return
      new IntersectionObserver((entries) => {
        this.galleryInView = entries.some((e) => e.isIntersecting)
      }, { rootMargin: '0px 0px -30% 0px' }).observe(gallery)
    })
  },

  thumb,
  large,
  formatDuration,

  get galleryCount() {
    return this.galleryFiles.length
  },

  get filteredGalleryFiles() {
    if (this.mediaFilter === 'all') return this.galleryFiles
    const wantVideo = this.mediaFilter === 'videos'
    return this.galleryFiles.filter((f) => !!f.isVideo === wantVideo)
  },

  get galleryNoun() {
    const count = this.filteredGalleryFiles.length
    if (this.mediaFilter === 'photos') return count === 1 ? 'photo' : 'photos'
    if (this.mediaFilter === 'videos') return count === 1 ? 'video' : 'videos'
    return count === 1 ? 'memory' : 'memories'
  },

  // Only the currently rendered window of the grid (grown on scroll)
  get visibleGalleryFiles() {
    return this.filteredGalleryFiles.slice(0, this.galleryLimit)
  },

  get lightboxFile() {
    return this.lightboxIndex === null ? null : this.filteredGalleryFiles[this.lightboxIndex]
  },

  openLightbox(index) {
    this.lightboxIndex = index
    document.documentElement.style.overflow = 'hidden'
    document.body.style.overflow = 'hidden'
  },

  closeLightbox() {
    this.lightboxIndex = null
    document.documentElement.style.overflow = ''
    document.body.style.overflow = ''
  },

  // Autoplay with sound when the browser allows it, muted otherwise
  playVideo(el) {
    el.play().catch(() => {
      el.muted = true
      el.play().catch(() => {})
    })
  },

  // Soft-hides one of this party's uploads from the public gallery
  // (recoverable from the admin panel — nothing is deleted)
  async removeFile() {
    const file = this.lightboxFile
    if (!file || !file.mine || this.removing) return
    if (!confirm('Remove this photo from the gallery?')) return
    this.removing = true
    try {
      const res = await fetch(`${API}/share-hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partyId: this.partyId, key: file.id })
      })
      if (!res.ok) throw new Error()
      const data = await res.json()
      // Ignore any cached listing generated before the hide
      if (data.hiddenAt) this.galleryGeneratedAt = Math.max(this.galleryGeneratedAt, data.hiddenAt)
      this.closeLightbox()
      this.galleryFiles = this.galleryFiles.filter((f) => f.id !== file.id)
    } catch {
      alert('Something went wrong — please try again.')
    } finally {
      this.removing = false
    }
  },

  lightboxStep(delta) {
    if (this.lightboxIndex === null) return
    const count = this.filteredGalleryFiles.length
    this.lightboxIndex = (this.lightboxIndex + delta + count) % count
  },

  // The head starts the very first gallery request before this bundle
  // has downloaded (see _head.erb); pick it up rather than fetch twice.
  // `fresh` adds a unique param so the request misses the edge cache —
  // used right after an upload, when the cached listing is known-stale.
  async refreshGallery({ fresh = false } = {}) {
    try {
      const params = new URLSearchParams()
      if (this.partyId) params.set('p', this.partyId)
      if (fresh) params.set('fresh', Date.now())
      const query = params.toString() ? `?${params}` : ''

      const prefetch = window.__galleryPrefetch
      window.__galleryPrefetch = null
      const res = await (prefetch && !fresh ? prefetch : fetch(`${API}/share-gallery${query}`))
      if (!res.ok) return
      const data = await res.json()
      if ((data.generatedAt || 0) < this.galleryGeneratedAt) return
      this.galleryGeneratedAt = data.generatedAt || 0

      // Computed once per refresh (not in reactive getters — re-sorting
      // on every render made the page laggy)
      let files = (data.groups || [])
        .flatMap((g) => g.files.map((f) => ({ ...f, label: g.label, mine: Boolean(g.mine) })))
        .sort((a, b) => shuffleRank(a.id) - shuffleRank(b.id))

      // Keep the objects we already rendered so Alpine doesn't re-render
      // the whole grid every 60 seconds when nothing changed.
      const existing = new Map(this.galleryFiles.map((f) => [f.id, f]))
      if (files.length === existing.size && files.every((f) => existing.has(f.id))) {
        this.galleryLoaded = true
        return
      }
      files = files.map((f) => existing.get(f.id) || f)

      const images = files.filter((f) => !f.isVideo)
      this.heroTiles = images.length === 0
        ? []
        : Array.from({ length: 24 }, (_, i) => images[i % images.length])

      this.galleryFiles = files
      this.galleryLoaded = true
    } catch { /* leave the previous gallery in place */ }
  },

  // A batch just landed in R2. Drop the edge-cached listing so everyone
  // else sees it within their next poll, and fetch past the cache
  // ourselves instead of waiting for the purge to propagate.
  async galleryChanged() {
    try {
      const res = await fetch(`${API}/share-gallery-purge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ partyId: this.partyId })
      })
      const data = res.ok ? await res.json() : {}
      if (data.purgedAt) this.galleryGeneratedAt = Math.max(this.galleryGeneratedAt, data.purgedAt)
    } catch { /* the cache expires on its own within a minute */ }
    await this.refreshGallery({ fresh: true })
  },

  lazyVideo(el, url) {
    el.dataset.src = `${url}#t=0.001`
    videoObserver.observe(el)
  },

  get doneCount() {
    return this.items.filter((i) => i.status === 'done').length
  },

  get uploading() {
    return this.items.some((i) => i.status === 'signing' || i.status === 'uploading')
  },

  get failedCount() {
    return this.items.filter((i) => i.status === 'failed').length
  },

  // Only the first few tiles are rendered; the rest collapse into a
  // "+N more" counter so huge selections don't blow up the DOM
  get visibleItems() {
    return this.items.slice(0, PREVIEW_LIMIT)
  },

  get hiddenItemCount() {
    return Math.max(0, this.items.length - PREVIEW_LIMIT)
  },

  // Aggregate state of the items collapsed into the "+N more" tile,
  // so the counter shows upload progress like the visible tiles do
  get hiddenItems() {
    return this.items.slice(PREVIEW_LIMIT)
  },

  get hiddenDoneCount() {
    return this.hiddenItems.filter((i) => i.status === 'done').length
  },

  get hiddenUploading() {
    return this.hiddenItems.some((i) =>
      i.status === 'pending' || i.status === 'signing' || i.status === 'uploading')
  },

  get hiddenAllDone() {
    return this.hiddenItemCount > 0 && this.hiddenDoneCount === this.hiddenItemCount
  },

  get hiddenProgress() {
    const items = this.hiddenItems
    if (items.length === 0) return 0
    const sum = items.reduce((acc, i) => acc + (i.status === 'done' ? 100 : i.progress), 0)
    return Math.round(sum / items.length)
  },

  addFiles(fileList) {
    if (!this.partyId) return
    this.error = ''
    this.notice = ''
    let added = 0
    let skipped = 0
    for (const file of fileList) {
      const type = fileType(file)
      const isMedia = type.startsWith('image/') || type.startsWith('video/')
      if (!isMedia) { skipped++; continue }
      added++
      const id = `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      rawFiles.set(id, file)
      const isVideo = type.startsWith('video/')
      const item = {
        id,
        type,
        name: file.name,
        size: file.size,
        isVideo,
        // Videos keep a plain object URL (preload="metadata" keeps them
        // cheap); image previews are generated as small thumbnails below
        previewUrl: isVideo ? URL.createObjectURL(file) : null,
        status: 'pending', // pending | signing | uploading | done | failed
        progress: 0
      }
      this.items.push(item)
      // Thumbnails only for tiles that will actually render
      if (!isVideo && this.items.length <= PREVIEW_LIMIT) this.thumbQueue.push(item)
    }
    this.pumpThumbs()
    if (added === 0) {
      this.error = 'Please choose photos or videos.'
      return
    }
    if (skipped > 0) {
      this.notice = `${skipped} ${skipped === 1 ? 'file was' : 'files were'} skipped — only photos and videos can be shared.`
    }
    this.maybeUpload()
  },

  pumpThumbs() {
    while (this.thumbActive < THUMB_CONCURRENCY && this.thumbQueue.length > 0) {
      const item = this.thumbQueue.shift()
      this.thumbActive++
      makeThumb(rawFiles.get(item.id))
        .then((url) => { item.previewUrl = url })
        .catch(() => {})
        .finally(() => {
          this.thumbActive--
          this.pumpThumbs()
        })
    }
  },

  async maybeUpload() {
    const pending = this.items.filter((i) => i.status === 'pending')
    if (pending.length === 0) return
    pending.forEach((i) => { i.status = 'signing' })
    this.error = ''

    // The presign endpoint accepts at most 30 files per request
    for (let i = 0; i < pending.length; i += 30) {
      await this.signAndQueue(pending.slice(i, i + 30))
    }
  },

  async signAndQueue(batch) {
    try {
      // Hash sequentially — parallel arrayBuffer() reads of a big batch
      // spike memory on mobile Safari
      for (const item of batch) {
        if (item.hash === undefined) item.hash = await hashFile(rawFiles.get(item.id))
      }

      // Same file selected twice in one session: mark the repeat done
      // without uploading (the server would map both to the same object)
      const seen = new Set()
      const duplicates = []
      const toSign = batch.filter((item) => {
        if (item.hash && seen.has(item.hash)) { duplicates.push(item); return false }
        if (item.hash) seen.add(item.hash)
        return true
      })
      duplicates.forEach((item) => {
        item.status = 'done'
        item.progress = 100
        rawFiles.delete(item.id)
      })
      if (toSign.length === 0) return

      const res = await fetch(`${API}/share-upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          partyId: this.partyId,
          files: toSign.map((i) => ({ name: i.name, type: i.type, size: i.size, hash: i.hash }))
        })
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        // Bad/stale invite link: drop into view-only mode
        if (data.code === 'invalid_party') this.partyId = ''
        throw new Error(data.error || 'Something went wrong. Please try again.')
      }

      const { uploads } = await res.json()
      toSign.forEach((item, idx) => {
        // Already in the bucket (uploaded on a previous visit) — no
        // need to send the bytes again
        if (uploads[idx].exists) {
          item.status = 'done'
          item.progress = 100
          rawFiles.delete(item.id)
          return
        }
        item.uploadUrl = uploads[idx].url
        item.status = 'uploading'
        this.queue.push(item)
      })
      this.pump()
      if (this.active === 0 && this.queue.length === 0) this.refreshGallery()
    } catch (e) {
      this.error = e.message
      batch.forEach((i) => { if (i.status === 'signing') i.status = 'failed' })
    }
  },

  pump() {
    while (this.active < CONCURRENCY && this.queue.length > 0) {
      const item = this.queue.shift()
      this.active++
      this.uploadOne(item).then(() => {
        this.active--
        this.pump()
        if (this.active === 0 && this.queue.length === 0) this.galleryChanged()
      })
    }
  },

  retryFailed() {
    this.items.forEach((i) => { if (i.status === 'failed') i.status = 'pending' })
    this.maybeUpload()
  },

  async uploadOne(item, attempt = 0) {
    const ok = await new Promise((resolve) => {
      const xhr = new XMLHttpRequest()
      xhr.open('PUT', item.uploadUrl)
      xhr.setRequestHeader('Content-Type', item.type)

      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return
        const progress = Math.round((e.loaded / e.total) * 100)
        // Skip no-op writes — each one re-renders the tile
        if (progress !== item.progress) item.progress = progress
      }
      xhr.onload = () => resolve(xhr.status >= 200 && xhr.status < 300)
      xhr.onerror = () => resolve(false)
      xhr.send(rawFiles.get(item.id))
    })

    // Mobile connections drop mid-batch all the time — retry once
    // before surfacing a failure (the presigned URL stays valid)
    if (!ok && attempt === 0) {
      item.progress = 0
      return this.uploadOne(item, 1)
    }

    item.status = ok ? 'done' : 'failed'
    if (ok) {
      item.progress = 100
      rawFiles.delete(item.id) // release the File so memory frees as we go
    }
  }
}))
